import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Search, CalendarIcon } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { getDisplayName } from '@/utils/profileUtils';
import { TalentSelector } from '@/components/TalentSelector';

const holdingTypes = ['All', 'Investment', 'Content', 'Social', 'Apparel', 'Advisory'] as const;
const timeRanges = ['Last 3M', '6M', 'YTD', '12M', 'Custom', 'All'] as const;

interface DashboardHeaderProps {
  selectedTimeRange: string;
  setSelectedTimeRange: (range: string) => void;
  selectedFilters: string[];
  toggleFilter: (filter: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedAthleteId?: string;
  setSelectedAthleteId?: (id: string) => void;
  customDateRange?: { from: Date | null; to: Date | null };
  setCustomDateRange?: (range: { from: Date | null; to: Date | null }) => void;
}

export const DashboardHeader = ({
  selectedTimeRange,
  setSelectedTimeRange,
  selectedFilters,
  toggleFilter,
  searchQuery,
  setSearchQuery,
  selectedAthleteId,
  setSelectedAthleteId,
  customDateRange,
  setCustomDateRange,
}: DashboardHeaderProps) => {
  const { currentUser, accountType } = useUser();
  const [customPickerOpen, setCustomPickerOpen] = useState(false);

  const displayName = currentUser ? getDisplayName(currentUser, accountType) : 'User';

  const handleTimeRangeClick = (range: string) => {
    if (range === 'Custom') {
      setCustomPickerOpen(true);
    }
    setSelectedTimeRange(range);
  };

  const formatDate = (d: Date | null) =>
    d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  // Format as MM/DD/YYYY for the typeable inputs so the user can easily
  // edit each component of the date.
  const formatInputDate = (d: Date | null) => {
    if (!d) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  // Parse MM/DD/YYYY or M/D/YYYY as local midnight so it matches the
  // Calendar component's date handling (no UTC/timezone drift).
  const parseInputDate = (s: string): Date | null => {
    const trimmed = s.trim();
    if (!trimmed) return null;
    const m = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    const month = Number(m[1]);
    const day = Number(m[2]);
    const year = Number(m[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const d = new Date(year, month - 1, day);
    // Guard against rollover (e.g. "02/30/2025" → March 2)
    if (d.getMonth() !== month - 1 || d.getDate() !== day) return null;
    return d;
  };

  // Local state for the typeable inputs so the user can edit freely
  // without each keystroke being parsed/rejected. On blur (or Enter) we
  // commit the parsed value to customDateRange.
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');

  // Keep the input strings in sync with the committed state whenever
  // the picker opens or the state changes from elsewhere (calendar clicks).
  React.useEffect(() => {
    setFromInput(formatInputDate(customDateRange?.from ?? null));
  }, [customDateRange?.from]);
  React.useEffect(() => {
    setToInput(formatInputDate(customDateRange?.to ?? null));
  }, [customDateRange?.to]);

  const commitFromInput = () => {
    const parsed = parseInputDate(fromInput);
    if (parsed && setCustomDateRange) {
      setCustomDateRange({ from: parsed, to: customDateRange?.to ?? null });
      setSelectedTimeRange('Custom');
    } else if (!fromInput.trim() && setCustomDateRange) {
      setCustomDateRange({ from: null, to: customDateRange?.to ?? null });
    } else {
      // Invalid input → revert to last committed value
      setFromInput(formatInputDate(customDateRange?.from ?? null));
    }
  };

  const commitToInput = () => {
    const parsed = parseInputDate(toInput);
    if (parsed && setCustomDateRange) {
      setCustomDateRange({ from: customDateRange?.from ?? null, to: parsed });
      setSelectedTimeRange('Custom');
    } else if (!toInput.trim() && setCustomDateRange) {
      setCustomDateRange({ from: customDateRange?.from ?? null, to: null });
    } else {
      setToInput(formatInputDate(customDateRange?.to ?? null));
    }
  };

  const customLabel =
    selectedTimeRange === 'Custom' && customDateRange?.from
      ? `${formatDate(customDateRange.from)}${customDateRange.to ? ' – ' + formatDate(customDateRange.to) : ' →'}`
      : 'Custom';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
            {displayName}'s Dashboard
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {accountType === 'talent' ? 'Track your performance and partnerships' :
             accountType === 'brand' ? 'Manage your campaigns and talent partnerships' :
             accountType === 'agent' ? 'Monitor your client portfolio' :
             'Overview of your platform activity'}
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        {/* Time Range Selector and Athlete Selector */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex gap-2 flex-wrap">
            {timeRanges.map((range) => {
              if (range === 'Custom') {
                return (
                  <Popover
                    key={range}
                    open={customPickerOpen}
                    onOpenChange={setCustomPickerOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant={selectedTimeRange === 'Custom' ? 'default' : 'outline'}
                        size="sm"
                        className="whitespace-nowrap gap-1"
                        onClick={() => handleTimeRangeClick('Custom')}
                      >
                        <CalendarIcon className="h-3 w-3" />
                        {customLabel}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <div className="px-3 pt-3 pb-3 space-y-3 flex flex-col items-center">
                        <p className="text-sm font-medium self-start">Select date range</p>
                        {/* Typeable date inputs — click and type MM/DD/YYYY */}
                        <div className="flex gap-2 items-end justify-center">
                          <div className="space-y-1">
                            <label className="text-xs text-muted-foreground block">From</label>
                            <Input
                              type="text"
                              placeholder="MM/DD/YYYY"
                              value={fromInput}
                              onChange={(e) => setFromInput(e.target.value)}
                              onBlur={commitFromInput}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  commitFromInput();
                                }
                              }}
                              className="h-8 text-sm w-28 px-2"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-muted-foreground block">To</label>
                            <Input
                              type="text"
                              placeholder="MM/DD/YYYY"
                              value={toInput}
                              onChange={(e) => setToInput(e.target.value)}
                              onBlur={commitToInput}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  commitToInput();
                                }
                              }}
                              className="h-8 text-sm w-28 px-2"
                            />
                          </div>
                        </div>
                        {/* Single range calendar — centered with its own internal padding */}
                        <Calendar
                          mode="range"
                          selected={{
                            from: customDateRange?.from ?? undefined,
                            to: customDateRange?.to ?? undefined,
                          }}
                          onSelect={(range) => {
                            if (setCustomDateRange) {
                              setCustomDateRange({
                                from: range?.from ?? null,
                                to: range?.to ?? null,
                              });
                            }
                            setSelectedTimeRange('Custom');
                          }}
                          numberOfMonths={1}
                          className="p-0 mx-auto"
                          classNames={{
                            day_today: '',
                          }}
                          initialFocus
                        />
                        <div className="flex justify-end gap-2 w-full">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (setCustomDateRange) setCustomDateRange({ from: null, to: null });
                            }}
                          >
                            Clear
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setCustomPickerOpen(false)}
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              }

              return (
                <Button
                  key={range}
                  variant={selectedTimeRange === range ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleTimeRangeClick(range)}
                  className="whitespace-nowrap"
                >
                  {range}
                </Button>
              );
            })}
          </div>

          {/* Talent Selector for Agents */}
          {accountType === 'agent' && selectedAthleteId !== undefined && setSelectedAthleteId && (
            <TalentSelector
              selectedAthleteId={selectedAthleteId}
              setSelectedAthleteId={setSelectedAthleteId}
            />
          )}
        </div>

        {/* Search */}
        <div className="relative w-full lg:w-80">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search holdings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {holdingTypes.map((type) => (
          <Badge
            key={type}
            variant={selectedFilters.includes(type) ? 'default' : 'outline'}
            className="cursor-pointer hover:bg-primary/20 transition-colors"
            onClick={() => toggleFilter(type)}
          >
            {type}
          </Badge>
        ))}
      </div>
    </div>
  );
};
